import { Column } from "@tanstack/react-table";
import CustomTooltip from "components/CustomTooltip";
import React, { useEffect, useRef, useState } from "react";

interface ColumnFilterProps<TData> {
  columns: Column<TData, unknown>[];
}

function ColumnFilter<TData>({ columns }: ColumnFilterProps<TData>) {
  const [showOrHide, setShowOrHide] = useState<"hidden" | "block">("hidden");
  const [searchQuery, setSearchQuery] = useState("");
  const [allSelected, setAllSelected] = useState(false);
  const [resetState, setResetState] = useState(false);
  const [previousVisibleIds, setPreviousVisibleIds] = useState<string[]>([]);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Store default visible columns
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      const originalIds = columns
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setPreviousVisibleIds(originalIds);
    }
  }, [columns, isInitialRender]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowOrHide("hidden");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Select all toggle
  const handleSelectAll = () => {
    const newCheckedState = !allSelected;
    columns.forEach((column) => {
      column.getToggleVisibilityHandler()({
        target: { checked: newCheckedState },
      });
    });
    setAllSelected(newCheckedState);
    setResetState(false);
  };

  // Reset to default
  const handleReset = () => {
    columns.forEach((column) => {
      column.getToggleVisibilityHandler()({
        target: { checked: previousVisibleIds.includes(column.id) },
      });
    });
    setResetState(true);
    setAllSelected(false);
  };

  // Filter columns by search query
  const filteredColumns = columns.filter((column) =>
    column.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Track if reset matches default state
  useEffect(() => {
    const currentVisibleIds = columns
      .filter((column) => column.getIsVisible())
      .map((column) => column.id);
    const isMatching =
      currentVisibleIds.length === previousVisibleIds.length &&
      currentVisibleIds.every((id) => previousVisibleIds.includes(id));
    setResetState(isMatching);
  }, [columns, previousVisibleIds]);

  // Toggle single column
  const handleColumnToggle = (column: Column<TData, unknown>) => {
    column.getToggleVisibilityHandler()({
      target: { checked: !column.getIsVisible() },
    });
    setAllSelected(false);
    setResetState(false);
  };

  return (
    <div ref={dropdownRef} className="relative w-max max-h-80">
      {/* Trigger Button */}
      <CustomTooltip title="Column Visibility" placement="left">
        <button
        type="button"
        onClick={() =>
          setShowOrHide(showOrHide === "hidden" ? "block" : "hidden")
        }
        className="text-[#333] text-sm border-none outline-none flex items-center justify-evenly space-x-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="w-4 h-4"
        >
          <path d="M6.5 2.25a.75.75 0 0 0-1.5 0v3a.75.75 0 0 0 1.5 0V4.5h6.75a.75.75 0 0 0 0-1.5H6.5v-.75ZM11 6.5a.75.75 0 0 0-1.5 0v3a.75.75 0 0 0 1.5 0v-.75h2.25a.75.75 0 0 0 0-1.5H11V6.5ZM5.75 10a.75.75 0 0 1 .75.75v.75h6.75a.75.75 0 0 1 0 1.5H6.5v.75a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 .75-.75ZM2.75 7.25H8.5v1.5H2.75a.75.75 0 0 1 0-1.5ZM4 3H2.75a.75.75 0 0 0 0 1.5H4V3ZM2.75 11.5H4V13H2.75a.75.75 0 0 1 0-1.5Z" />
        </svg>
        <span>Columns</span>
      </button>
      </CustomTooltip>

      {/* Dropdown */}
      <ul
        className={`absolute ${showOrHide} shadow-lg bg-primary-50 py-2 px-2 z-[1000] min-w-full w-max rounded max-h-96 overflow-auto right-0 top-8 `}
      >
        {/* Search */}
        <li className="mb-2">
          <input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-1.5 w-full rounded text-black text-sm border-none outline-primary-50 focus:bg-transparent"
          />
        </li>

        {/* Select All + Reset */}
        <li className="mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="w-4 ml-4"
                checked={allSelected}
                onChange={handleSelectAll}
              />
              <label className="text-sm ml-2">All</label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                className="w-4 mr-2"
                checked={resetState}
                onChange={handleReset}
              />
              <label className="text-sm">Default</label>
            </div>
          </div>
        </li>

        {/* Column List */}
        {filteredColumns.map((column) => (
          <li
            key={column.id}
            className="py-1 px-4 hover:bg-blue-50 rounded-sm text-black text-sm cursor-pointer overflow-auto hide-scrollbar "
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                className="w-4"
                checked={column.getIsVisible()}
                onChange={() => handleColumnToggle(column)}
              />
              <label className="text-sm ml-4">
                {String(column.id ?? column.id)}
              </label>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ColumnFilter;
